// @ts-nocheck

/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */

import MacheteConstants from '@hoverinc/machete-constants';

import { config } from 'src/config';
import { FeatureCategories, Menu } from 'src/modules/designStudio/constants';
import { EventType, emitPageAction } from 'src/shared/telemetry';
import { isMobileApp } from 'src/shared/utils/environment';
import { configureRetry } from 'src/shared/utils/retry';

import type {
  ExtendedMacheteViewer,
  FocusOptionsType,
  IRenderMacheteParams,
  MacheteType,
} from './types';

const DEFAULT_FOCUS_OPTS = {
  moneyshotConfig: {
    offset: 1.2,
  },
};

const initViewerParams = () => {
  // Determine quality level.
  const macheteQualityTreatment = 'low';

  const QUALITY_FEATURE_FLAG = {
    ultra_high_plus: MacheteConstants.Quality.ULTRA_HIGH_PLUS,
    ultra_high: MacheteConstants.Quality.ULTRA_HIGH,
    high: MacheteConstants.Quality.HIGH,
    medium: MacheteConstants.Quality.MEDIUM,
    low_plus: MacheteConstants.Quality.LOW_PLUS,
    low: MacheteConstants.Quality.LOW,
  };

  const macheteQualityOptionURLParameter = new URLSearchParams(
    location.search,
  ).get('macheteQualityOption');

  let macheteQualityOption = MacheteConstants.Quality.LOW;

  if (macheteQualityTreatment in QUALITY_FEATURE_FLAG) {
    macheteQualityOption = QUALITY_FEATURE_FLAG[macheteQualityTreatment];
  }

  if (
    macheteQualityOptionURLParameter &&
    macheteQualityOptionURLParameter in QUALITY_FEATURE_FLAG
  ) {
    macheteQualityOption =
      QUALITY_FEATURE_FLAG[macheteQualityOptionURLParameter];
  }

  // Determine environment type.

  const ENVIRONMENT_TYPE_FEATURE_FLAG = {
    default: MacheteConstants.EnvironmentTypes.Default,
    gradient: MacheteConstants.EnvironmentTypes.Gradient,
    grass_field: MacheteConstants.EnvironmentTypes.GrassField,
  };

  const macheteEnvironmentTypeURLParameter = new URLSearchParams(
    location.search,
  ).get('macheteEnvironmentType');

  let macheteEnvironmentType = MacheteConstants.EnvironmentTypes.Gradient;

  if (
    macheteEnvironmentTypeURLParameter &&
    macheteEnvironmentTypeURLParameter in ENVIRONMENT_TYPE_FEATURE_FLAG
  ) {
    macheteEnvironmentType =
      ENVIRONMENT_TYPE_FEATURE_FLAG[macheteEnvironmentTypeURLParameter];
  }

  return {
    /**
     * To see all the options Machete allows to configure, check the interface definition at:
     * https://github.com/hoverinc/machete/blob/master/src/entities/Environment.ts#L8
     */
    quality: macheteQualityOption,
    useProductsLibraryForTexturing: true,
    webgl: {
      context: {
        antialias: true,
        alpha: true,
        preserveDrawingBuffer: true,
        premultipliedAlpha: true,
        autoClear: false,
      },
    },
    useOrbit: true,
    usePointerZoom: false,
    clearColorAlpha: 0.0,
    shadowOnlyBG: true,
    backgroundColor: '#000000',
    environmentType: macheteEnvironmentType,
    backgroundTexture:
      'https://machete-terrain.hover.to/morningBrightCloudySky.jpeg',
    terrainTexture:
      'https://machete-terrain.hover.to/grassFieldTerrainTexture.jpeg',
    controlOptions: {
      maxPolarAngle:
        MacheteConstants.EnvironmentPresets[macheteEnvironmentType]
          .controlsMaxPolarAngle,
      noPan: true,
      maxDistance: 30,
    },
    renderOptions: {
      clearColor: 0,
    },
  };
};

const listenForEventFactory =
  viewer => (event: string, callback: () => void) => {
    viewer.controls.addEventListener(event, callback);

    // unsubscriber
    return () => {
      viewer.controls.removeEventListener(event, callback);
    };
  };

const getLabelsForCategory = (category: FeatureCategories) => {
  const {
    WALL_LABELS,
    ROOF_LABEL_ID,
    OPENINGS_TRIM_LABEL_ID,
    TRIM_LABEL_ID,
    WINDOW_LABEL_ID,
  } = MacheteConstants;

  let labels: number[];

  switch (category) {
    case FeatureCategories.WALL:
      labels = WALL_LABELS;
      break;

    case FeatureCategories.ROOF:
      labels = [ROOF_LABEL_ID];
      break;

    case FeatureCategories.TRIM:
      labels = [OPENINGS_TRIM_LABEL_ID, TRIM_LABEL_ID];
      break;

    case FeatureCategories.WINDOW:
      labels = [WINDOW_LABEL_ID];
      break;

    default:
      break;
  }

  return labels;
};

const getCategoryForFeatureLabel = featureLabel => {
  const labelsWall = getLabelsForCategory(FeatureCategories.WALL);
  const labelsRoof = getLabelsForCategory(FeatureCategories.ROOF);
  const labelsTrim = getLabelsForCategory(FeatureCategories.TRIM);
  const labelsWindow = getLabelsForCategory(FeatureCategories.WINDOW);

  // See DS-846
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  if (labelsRoof.includes(featureLabel)) {
    return FeatureCategories.ROOF;
  }
  // See DS-846
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  if (labelsTrim.includes(featureLabel)) {
    return FeatureCategories.TRIM;
  }
  // See DS-846
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  if (labelsWall.includes(featureLabel)) {
    return FeatureCategories.WALL;
  }
  // See DS-846
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  if (labelsWindow.includes(featureLabel)) {
    return FeatureCategories.WINDOW;
  }
  return null;
};

const getOriginalFeatureStyles = buildingDisplay => {
  const allFeatures = [...buildingDisplay.features];

  // Save original Data for walls, roof, trim for reset
  const originalFeaturesStyles = allFeatures.reduce(
    (acc, feature) => {
      const category = getCategoryForFeatureLabel(feature.primary_label);

      if (category === FeatureCategories.WINDOW) {
        acc[category].push({
          uuid: feature.uuid,
          color:
            feature.appearance_preset.parametricOptions.attributes.windowColor,
          frameType:
            feature.appearance_preset.parametricOptions.attributes
              .windowProduct,
          gridType:
            feature.appearance_preset.parametricOptions.attributes.windowGrille,
        });
      } else if (category) {
        acc[category].push({
          uuid: feature.uuid,
          color: feature.color,
          textureId: feature.appearance_preset.textureID,
        });
      }
      return acc;
    },
    {
      [FeatureCategories.WALL]: [],
      [FeatureCategories.TRIM]: [],
      [FeatureCategories.ROOF]: [],
      [FeatureCategories.WINDOW]: [],
    },
  );

  return originalFeaturesStyles;
};

const findBiggestFeature = features => {
  let biggestFeature = features[0];
  let maxArea = 0;
  features.forEach(feature => {
    if (maxArea < feature.area.in_square_feet) {
      maxArea = feature.area.in_square_feet;
      biggestFeature = feature;
    }
  });

  return biggestFeature;
};

const getFocusOptionsForMenu = async (building, viewer, menu) => {
  const focusConfigs = {
    [Menu.ROOF]: {
      moneyshotConfig: {
        offset: 1.5,
      },
    },
    [Menu.WALL]: {
      side: MacheteConstants.FrontMoneyshot,
      offset: 0.65,
    },
    [Menu.WINDOW]: {
      moneyshotConfig: {
        offset: 1.8,
      },
    },
  };
  const focusConfig = menu === null ? DEFAULT_FOCUS_OPTS : focusConfigs[menu];
  const focusOptions: FocusOptionsType = {
    useOrbit: true,
    duration: 750,
    ...focusConfig,
  };

  let focusElement: object = building;

  if (menu === Menu.ROOF) {
    const roofFeatures = await building.findFeaturesByAttributes({
      primary_label: MacheteConstants.ROOF_LABEL_ID,
    });

    // On complicated roofs find the biggest feature to zoom in on
    const biggestRoofFeature = findBiggestFeature(roofFeatures);

    [focusElement] = [biggestRoofFeature];
  } else if (menu === Menu.WINDOW) {
    const windowFeatures = await building.findFeaturesByAttributes({
      primary_label: MacheteConstants.WINDOW_LABEL_ID,
      is_editable: true,
    });

    [focusElement] = windowFeatures;
  }

  return { focusElement, focusOptions };
};

const timeout = ms =>
  new Promise(resolve => {
    // See DS-846
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    setTimeout(resolve, ms);
  });

const changeCameraFocus = async (viewer, focusElement, focusOptions, delay) => {
  // allow menu open/close transition to complete
  await timeout(delay);

  viewer.focus(focusElement, focusOptions);
  viewer.controls.useOrbitMode = false;
  viewer.controls.update();

  await timeout(focusOptions.duration);

  viewer.controls.useOrbitMode = true;
  viewer.controls.update();
};

const renderMachete = async ({
  canvasRef,
  textures,
  geometry,
  metadata,
  mode,
}: IRenderMacheteParams): Promise<MacheteType> => {
  const { withRetry } = configureRetry();
  const MacheteDisplay = (
    await withRetry(
      () =>
        import(
          /* webpackChunkName: "machete" */
          '@hoverinc/machete-display'
        ),
    )()
  ).default;

  const viewerParams = initViewerParams();

  const buildingDisplay = new MacheteDisplay(
    canvasRef.current, // domElement
    viewerParams, // macheteOptions
    textures, // productCatalog
    {
      geometry,
      metadata,
      dimensionality: 3,
      useSyntheticGlassAnnotations: true,
      autotexture: true,
    }, // buildingData
    'design-studio',
    config.APP_VERSION,
    config.NODE_ENV,
  );

  await buildingDisplay.isReady();

  const { viewer, building } = buildingDisplay;

  emitPageAction(EventType.UNTEXTURED_BUILDING_LOADED, {
    designStudioMode: mode,
  });

  emitPageAction(EventType.TEXTURES_APPLIED, { designStudioMode: mode });

  // Save original Data for walls, roof, trim for reset
  const originalFeaturesStyles = getOriginalFeatureStyles(buildingDisplay);

  return { building, viewer, originalFeaturesStyles };
};

const generateAppearancePreset = (building, style) => {
  let newAppearance;
  if (style.textureId) {
    newAppearance = building.makeAppearancePresetFromTextureID(style.textureId);
  } else {
    newAppearance = building.makeAppearancePresetFromParametricAttributes({
      gridType: style.gridType,
      frameType: style.frameType,
    });
  }

  newAppearance.color = style.color;
  return newAppearance;
};

const resetStylesToOriginal = ({ building, ogStyles }) => {
  ogStyles.forEach(feature => {
    const featureWithUUID = building.getFeatureByUUID(feature.uuid);
    featureWithUUID.appearance_preset = generateAppearancePreset(
      building,
      feature,
    );
  });
};

const isStyleEmpty = ({ category, update }) => {
  if (category === FeatureCategories.WINDOW) {
    return (
      update.color === null &&
      update.gridType === null &&
      update.frameType === null
    );
  }
  return update.color === null && update.textureId === null;
};

const updateStyles = ({
  building,
  category,
  update,
  originalFeaturesStyles,
}) => {
  if (isStyleEmpty({ category, update })) {
    const ogStyles = originalFeaturesStyles[category];
    resetStylesToOriginal({ building, ogStyles });
    return;
  }
  const allFeatures = building.getFeatures();

  // See DS-846
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  const labels = getLabelsForCategory(category);

  const features = allFeatures.filter(feature =>
    // See DS-846
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    labels.includes(feature.primary_label),
  );

  const newAppearance = generateAppearancePreset(building, update);

  features.forEach(feature => {
    feature.appearance_preset = newAppearance;
  });
};

const takeScreenshot = async (viewer): Promise<Blob> => {
  const maintainAspectRatio = true;
  const screenshot = await viewer.takeScreenshot({
    maintainAspectRatio,
  });

  return screenshot;
};

const makeTransparentWithOutlines = building => {
  const features = building.getFeatures();
  for (let i = 0; i < features.length; i += 1) {
    const feature = features[i];
    feature.display_outline = true;
    feature.outline_color = '#FFFFFF';
    feature.occlusive_opacity = 1;
    if (isMobileApp()) {
      feature.outline_thickness = 0.1;
    }
  }
};

const restoreOpacityAndHideOutlines = building => {
  const features = building.getFeatures();
  for (let i = 0; i < features.length; i += 1) {
    const feature = features[i];
    feature.display_outline = false;
    feature.outline_color = '#FFFFFF';
    feature.occlusive_opacity = 0;
    if (isMobileApp()) {
      feature.outline_thickness = 0.0127;
    }
  }
};

const hideEnvironment = viewer => {
  viewer.environment.visible = false;
};

const showEnvironment = viewer => {
  viewer.environment.visible = true;
};

export {
  changeCameraFocus,
  findBiggestFeature,
  getCategoryForFeatureLabel,
  getFocusOptionsForMenu,
  getLabelsForCategory,
  listenForEventFactory,
  renderMachete,
  takeScreenshot,
  updateStyles,
  makeTransparentWithOutlines,
  restoreOpacityAndHideOutlines,
  hideEnvironment,
  showEnvironment,
};

export type {
  ExtendedMacheteViewer,
  FocusOptionsType,
  IRenderMacheteParams,
  MacheteType,
};
